import { FormEvent, useContext, useMemo, useState } from 'react';
import { BarChart3, CheckCircle2, Database, Download, ExternalLink, FileVideo, Target, Trash2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { LanguageContext } from '@/context/LanguageContext';
import { AuthContext } from '@/context/AuthContext';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import DatePicker from '@/components/ui/DatePicker';
import Input from '@/components/ui/Input';
import SideDrawer from '@/components/ui/SideDrawer';
import {
  useContentProductivity,
  useContentProductivityDetails,
  useContentContributors,
  useContentUploadQuotas,
  useDeleteContentUploadQuota,
  useSaveContentUploadQuota,
} from '@/hooks/useContentProductivity';
import {
  ContentUploadQuota,
  ProductivityDetailStatus,
  ProductivityContentType,
  SaveContentUploadQuota,
  UserProductivity,
} from '@/services/contentProductivityService';

const iso = (date: Date) => date.toISOString().slice(0, 10);
const startOfWeek = (date: Date) => {
  const result = new Date(date);
  const day = result.getDay() || 7;
  result.setDate(result.getDate() - day + 1);
  return result;
};

const presetDates = (preset: string) => {
  const now = new Date();
  if (preset === 'thisWeek') return { from: iso(startOfWeek(now)), to: iso(now) };
  if (preset === 'lastWeek') {
    const end = new Date(startOfWeek(now));
    end.setDate(end.getDate() - 1);
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    return { from: iso(start), to: iso(end) };
  }
  if (preset === 'lastMonth') {
    return {
      from: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      to: iso(new Date(now.getFullYear(), now.getMonth(), 0)),
    };
  }
  return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
};

const formatBytes = (bytes: number) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index > 2 ? 1 : 0)} ${units[index]}`;
};

const emptyQuota = (from: string): SaveContentUploadQuota => ({
  userId: 0,
  periodStart: from,
  periodEnd: undefined,
  recurrenceType: 'MONTHLY',
  contentType: 'ALL',
  metricType: 'CONTENT_COMPLETED',
  targetQuantity: 1,
  notes: '',
});

const ContentProductivityReport = () => {
  const { t } = useContext(LanguageContext)!;
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const canManageGoals = user?.roleCode === 'admin' || user?.roleCode === 'superadmin';
  const copy = t.contentProductivity;
  const initial = presetDates('thisMonth');
  const [filters, setFilters] = useState({
    ...initial,
    contentType: 'ALL' as ProductivityContentType,
    userId: undefined as number | undefined,
  });
  const [tab, setTab] = useState<'overview' | 'quotas'>('overview');
  const [editingId, setEditingId] = useState<number>();
  const [quota, setQuota] = useState<SaveContentUploadQuota>(emptyQuota(initial.from));
  const [detailRow, setDetailRow] = useState<UserProductivity>();
  const [detailStatus, setDetailStatus] = useState<ProductivityDetailStatus>('ALL');
  const [detailSearch, setDetailSearch] = useState('');
  const [detailPage, setDetailPage] = useState(0);

  const reportQuery = useContentProductivity(filters);
  const contributorsQuery = useContentContributors();
  const quotasQuery = useContentUploadQuotas({
    from: filters.from,
    to: filters.to,
    userId: filters.userId,
  });
  const saveQuota = useSaveContentUploadQuota();
  const deleteQuota = useDeleteContentUploadQuota();
  const detailQuery = useContentProductivityDetails(detailRow ? {
    from: filters.from,
    to: filters.to,
    userId: detailRow.userId,
    contentType: detailRow.contentType,
    status: detailStatus,
    search: detailSearch || undefined,
    _page: detailPage,
    _size: 15,
  } : undefined);

  const contributors = useMemo(() => {
    return (contributorsQuery.data || [])
      .map((row) => ({ id: row.id, name: row.fullName || row.username }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [contributorsQuery.data]);

  const applyPreset = (preset: string) => {
    const dates = presetDates(preset);
    setFilters((current) => ({ ...current, ...dates }));
  };

  const submitQuota = async (event: FormEvent) => {
    event.preventDefault();
    if (!quota.userId) return toast.error(copy.selectContributor);
    try {
      await saveQuota.mutateAsync({ id: editingId, quota });
      toast.success(editingId ? copy.quotaUpdated : copy.quotaCreated);
      setEditingId(undefined);
      setQuota(emptyQuota(filters.from));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : copy.saveError);
    }
  };

  const editQuota = (item: ContentUploadQuota) => {
    setEditingId(item.id);
    setQuota({
      userId: item.userId,
      periodStart: item.periodStart,
      periodEnd: item.periodEnd,
      recurrenceType: item.recurrenceType,
      contentType: item.contentType,
      metricType: item.metricType,
      targetQuantity: item.targetQuantity,
      notes: item.notes || '',
    });
    setTab('quotas');
  };

  const removeQuota = async (id: number) => {
    if (!window.confirm(copy.confirmDelete)) return;
    try {
      await deleteQuota.mutateAsync(id);
      toast.success(copy.quotaDeleted);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : copy.deleteError);
    }
  };

  const exportCsv = () => {
    if (!reportQuery.data) return;
    const rows = [
      ['user', 'username', 'contentType', 'filesUploaded', 'contentCompleted', 'validFiles', 'invalidFiles', 'uploadedBytes'],
      ...reportQuery.data.users.map((row) => [
        row.fullName, row.username, row.contentType, row.filesUploaded, row.contentCompleted,
        row.validFiles, row.invalidFiles, row.uploadedBytes,
      ]),
    ];
    const blob = new Blob([rows.map((row) => row.join(',')).join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `content-productivity-${filters.from}-${filters.to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const openDetails = (row: UserProductivity, status: ProductivityDetailStatus) => {
    setDetailRow(row);
    setDetailStatus(status);
    setDetailSearch('');
    setDetailPage(0);
  };

  const changeDetailStatus = (status: ProductivityDetailStatus) => {
    setDetailStatus(status);
    setDetailPage(0);
  };

  const summary = reportQuery.data?.summary;
  const cards = [
    [copy.filesUploaded, summary?.filesUploaded || 0, FileVideo],
    [copy.contentCompleted, summary?.contentCompleted || 0, CheckCircle2],
    [copy.uploadedSize, formatBytes(summary?.uploadedBytes || 0), Database],
    [copy.contributors, summary?.activeContributors || 0, Users],
  ] as const;
  const maxDaily = Math.max(1, ...(reportQuery.data?.dailyActivity.map((item) => item.filesUploaded) || []));

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{copy.title}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{copy.subtitle}</p>
        </div>
        <Button variant="outline" onClick={exportCsv} disabled={!reportQuery.data?.users.length}>
          <Download className="w-4 h-4 mr-2" /> {copy.exportCsv}
        </Button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 grid gap-3 md:grid-cols-5">
        <Select
          className="dark:bg-gray-900 dark:text-white"
          onChange={(e) => applyPreset(e.target.value)}
          defaultValue="thisMonth"
          options={[
            { value: 'thisWeek', label: copy.thisWeek },
            { value: 'lastWeek', label: copy.lastWeek },
            { value: 'thisMonth', label: copy.thisMonth },
            { value: 'lastMonth', label: copy.lastMonth },
          ]}
        />
        <DatePicker
          value={filters.from}
          aria-label={copy.from}
          onChange={(e) => setFilters({ ...filters, from: e.target.value })}
        />
        <DatePicker
          value={filters.to}
          aria-label={copy.to}
          onChange={(e) => setFilters({ ...filters, to: e.target.value })}
        />
        <Select
          className="dark:bg-gray-900 dark:text-white"
          value={filters.contentType}
          onChange={(e) => setFilters({ ...filters, contentType: e.target.value as ProductivityContentType })}
          options={[
            { value: 'ALL', label: copy.allContent },
            { value: 'MOVIE', label: copy.movies },
            { value: 'EPISODE', label: copy.episodes },
          ]}
        />
        <Select
          className="dark:bg-gray-900 dark:text-white"
          value={filters.userId || ''}
          allowEmpty
          emptyOptionLabel={copy.allContributors}
          onChange={(e) => setFilters({ ...filters, userId: e.target.value ? Number(e.target.value) : undefined })}
          options={contributors.map((user) => ({ value: String(user.id), label: user.name }))}
        />
      </div>

      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        <button className={`px-4 py-2 ${tab === 'overview' ? 'border-b-2 border-[color:var(--brand-primary)] font-semibold' : ''}`} onClick={() => setTab('overview')}>
          <BarChart3 className="inline w-4 h-4 mr-2" />{copy.overview}
        </button>
        <button className={`px-4 py-2 ${tab === 'quotas' ? 'border-b-2 border-[color:var(--brand-primary)] font-semibold' : ''}`} onClick={() => setTab('quotas')}>
          <Target className="inline w-4 h-4 mr-2" />{copy.goals}
        </button>
      </div>

      {tab === 'overview' ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map(([label, value, Icon]) => (
              <div key={label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <Icon className="w-5 h-5 text-[color:var(--brand-primary)]" />
                <p className="text-sm text-gray-500 mt-3">{label}</p>
                <p className="text-2xl font-bold dark:text-white">{value}</p>
              </div>
            ))}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h2 className="font-semibold mb-4 dark:text-white">{copy.dailyActivity}</h2>
            <div className="h-44 flex items-end gap-1 overflow-x-auto">
              {reportQuery.data?.dailyActivity.map((item) => (
                <div key={`${item.date}-${item.contentType}`} className="min-w-8 flex-1 group" title={`${item.date}: ${item.filesUploaded}`}>
                  <div className={item.contentType === 'MOVIE' ? 'bg-blue-500 rounded-t' : 'bg-violet-500 rounded-t'}
                    style={{ height: `${Math.max(4, item.filesUploaded / maxDaily * 140)}px` }} />
                  <div className="text-[10px] text-center text-gray-500 mt-1">{item.date.slice(8)}</div>
                </div>
              ))}
              {!reportQuery.data?.dailyActivity.length && <p className="m-auto text-gray-500">{copy.noData}</p>}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 text-left">
                <tr>{[copy.contributor, copy.type, copy.files, copy.completed, copy.valid, copy.invalid, copy.size, copy.completionPercentage].map((header) =>
                  <th key={header} className="px-4 py-3 font-semibold">{header}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {reportQuery.data?.users.map((row) => (
                  <tr
                    key={`${row.userId}-${row.contentType}`}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    onClick={() => openDetails(row, 'ALL')}
                  >
                    <td className="px-4 py-3"><div className="font-medium">{row.fullName}</div><div className="text-gray-500">@{row.username}</div></td>
                    <td className="px-4 py-3">{row.contentType === 'MOVIE' ? copy.movies : copy.episodes}</td>
                    <td className="px-4 py-3"><button className="font-medium hover:underline" onClick={(event) => { event.stopPropagation(); openDetails(row, 'FILES_UPLOADED'); }}>{row.filesUploaded}</button></td>
                    <td className="px-4 py-3"><button className="font-medium hover:underline" onClick={(event) => { event.stopPropagation(); openDetails(row, 'CONTENT_COMPLETED'); }}>{row.contentCompleted}</button></td>
                    <td className="px-4 py-3 text-green-600"><button className="font-medium hover:underline" onClick={(event) => { event.stopPropagation(); openDetails(row, 'VALID'); }}>{row.validFiles}</button></td>
                    <td className="px-4 py-3 text-red-600"><button className="font-medium hover:underline" onClick={(event) => { event.stopPropagation(); openDetails(row, 'INVALID'); }}>{row.invalidFiles}</button></td>
                    <td className="px-4 py-3">{formatBytes(row.uploadedBytes)}</td>
                    <td className="px-4 py-3 min-w-48">
                      {(row.goals ?? []).length ? (row.goals ?? []).map((goal) => (
                        <div key={goal.goalId} className="not-last:mb-2">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>
                              {goal.achievedQuantity}/{goal.targetQuantity}
                            </span>
                            <span>{Math.round(goal.progressPercentage)}%</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${goal.progressPercentage >= 100 ? 'bg-green-500' : 'bg-[color:var(--brand-primary)]'}`}
                              style={{ width: `${goal.progressPercentage}%` }}
                            />
                          </div>
                        </div>
                      )) : <span className="text-gray-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {reportQuery.isLoading && <p className="p-6 text-center text-gray-500">{copy.loading}</p>}
            {!reportQuery.isLoading && !reportQuery.data?.users.length && <p className="p-6 text-center text-gray-500">{copy.noData}</p>}
          </div>
        </>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
          {canManageGoals && <form onSubmit={submitQuota} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4 h-fit">
            <h2 className="font-semibold dark:text-white">{editingId ? copy.editGoal : copy.assignGoal}</h2>
            <Select
              label={copy.contributor}
              required
              className="dark:bg-gray-900 dark:text-white"
              placeholder={copy.selectContributor}
              value={quota.userId ? String(quota.userId) : ''}
              onChange={(e) => setQuota({ ...quota, userId: Number(e.target.value) })}
              options={contributors.map((user) => ({ value: String(user.id), label: user.name }))}
            />
            <Select
              label={copy.frequency}
              className="dark:bg-gray-900 dark:text-white"
              value={quota.recurrenceType}
              onChange={(e) => setQuota({
                ...quota,
                recurrenceType: e.target.value as SaveContentUploadQuota['recurrenceType'],
                periodEnd: e.target.value === 'ONE_TIME' ? quota.periodEnd : undefined,
              })}
              options={[
                { value: 'WEEKLY', label: copy.weekly },
                { value: 'MONTHLY', label: copy.monthly },
                ...(quota.recurrenceType === 'ONE_TIME'
                  ? [{ value: 'ONE_TIME', label: copy.oneTimeLegacy }]
                  : []),
              ]}
            />
            <DatePicker
              label={copy.effectiveStart}
              required
              value={quota.periodStart}
              onChange={(e) => setQuota({ ...quota, periodStart: e.target.value })}
            />
            {quota.recurrenceType === 'ONE_TIME' && (
              <DatePicker
                label={copy.to}
                required
                value={quota.periodEnd || ''}
                onChange={(e) => setQuota({ ...quota, periodEnd: e.target.value })}
              />
            )}
            <Select
              label={copy.type}
              className="dark:bg-gray-900 dark:text-white"
              value={quota.contentType}
              onChange={(e) => setQuota({ ...quota, contentType: e.target.value as ProductivityContentType })}
              options={[
                { value: 'ALL', label: copy.allContent },
                { value: 'MOVIE', label: copy.movies },
                { value: 'EPISODE', label: copy.episodes },
              ]}
            />
            <Select
              label={copy.metric}
              className="dark:bg-gray-900 dark:text-white"
              value={quota.metricType}
              onChange={(e) => setQuota({ ...quota, metricType: e.target.value as SaveContentUploadQuota['metricType'] })}
              options={[
                { value: 'CONTENT_COMPLETED', label: copy.contentCompleted },
                { value: 'FILES_UPLOADED', label: copy.filesUploaded },
              ]}
            />
            <Input
              label={copy.target}
              required
              min={1}
              type="number"
              className="dark:bg-gray-900 dark:border-gray-600 dark:text-white"
              value={quota.targetQuantity}
              onChange={(e) => setQuota({ ...quota, targetQuantity: Number(e.target.value) })}
            />
            <label className="block space-y-1">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">{copy.notes}</span>
              <textarea
                rows={4}
                className="
                  w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm
                  focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                  disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                  dark:bg-gray-900 dark:border-gray-600 dark:text-white
                "
                value={quota.notes}
                onChange={(e) => setQuota({ ...quota, notes: e.target.value })}
              />
            </label>
            <div className="flex gap-2">
              <Button type="submit" loading={saveQuota.isPending}>{editingId ? copy.update : copy.assign}</Button>
              {editingId && <Button type="button" variant="outline" onClick={() => { setEditingId(undefined); setQuota(emptyQuota(filters.from)); }}>{copy.cancel}</Button>}
            </div>
          </form>}

          <div className={`space-y-3 ${canManageGoals ? '' : 'lg:col-span-2'}`}>
            {quotasQuery.data?.map((item) => (
              <div key={item.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between gap-4">
                <button className="text-left flex-1" onClick={() => canManageGoals && editQuota(item)}>
                  <div className="font-semibold dark:text-white">{item.fullName || item.username}</div>
                  <div className="text-sm text-gray-500">
                    {item.recurrenceType === 'WEEKLY' ? copy.weekly : item.recurrenceType === 'MONTHLY' ? copy.monthly : copy.oneTimeLegacy}
                    {' · '}{item.currentPeriodStart} – {item.currentPeriodEnd}
                    {' · '}
                    {item.contentType === 'ALL' ? copy.allContent : item.contentType === 'MOVIE' ? copy.movies : copy.episodes}
                    {' · '}
                    {item.metricType === 'FILES_UPLOADED' ? copy.filesUploaded : copy.contentCompleted}
                  </div>
                  <div className="mt-2"><span className="text-2xl font-bold">{item.targetQuantity}</span> <span className="text-sm text-gray-500">{copy.targetUnits}</span></div>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>{item.achievedQuantity} / {item.targetQuantity}</span>
                      <span>{Math.round(item.progressPercentage)}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                      <div className={`h-full rounded-full ${item.progressPercentage >= 100 ? 'bg-green-500' : 'bg-[color:var(--brand-primary)]'}`}
                        style={{ width: `${item.progressPercentage}%` }} />
                    </div>
                  </div>
                  {item.notes && <p className="text-sm mt-2 text-gray-600 dark:text-gray-300">{item.notes}</p>}
                </button>
                {canManageGoals && (
                  <Button variant="danger" size="xs" onClick={() => removeQuota(item.id)} aria-label={copy.delete}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            {quotasQuery.isLoading && <p className="text-center text-gray-500">{copy.loading}</p>}
            {!quotasQuery.isLoading && !quotasQuery.data?.length && <p className="text-center text-gray-500 p-8">{copy.noGoals}</p>}
          </div>
        </div>
      )}

      <SideDrawer
        isOpen={!!detailRow}
        onClose={() => setDetailRow(undefined)}
        title={detailRow ? `${detailRow.fullName} · ${detailRow.contentType === 'MOVIE' ? copy.movies : copy.episodes}` : ''}
        subtitle={`${filters.from} – ${filters.to}`}
      >
        <div className="p-5 space-y-4">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {([
              ['ALL', copy.all],
              ['FILES_UPLOADED', copy.filesUploaded],
              ['CONTENT_COMPLETED', copy.contentCompleted],
              ['VALID', copy.valid],
              ['INVALID', copy.invalid],
            ] as [ProductivityDetailStatus, string][]).map(([status, label]) => (
              <button
                key={status}
                type="button"
                onClick={() => changeDetailStatus(status)}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm ${
                  detailStatus === status
                    ? 'bg-[color:var(--brand-primary)] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <Input
            value={detailSearch}
            placeholder={copy.searchContent}
            className="dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            onChange={(event) => {
              setDetailSearch(event.target.value);
              setDetailPage(0);
            }}
          />

          {detailQuery.isLoading && <p className="py-10 text-center text-gray-500">{copy.loading}</p>}
          {!detailQuery.isLoading && !detailQuery.data?.content.length && (
            <p className="py-10 text-center text-gray-500">{copy.noDetails}</p>
          )}

          <div className="space-y-3">
            {detailQuery.data?.content.map((item) => (
              <div key={`${item.contentId}-${item.mediaLinkId || 'content'}`} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {item.parentTitle && <p className="text-xs text-gray-500 truncate">{item.parentTitle}</p>}
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">{item.title}</h3>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs">
                      {item.quality && <span className="rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-1">{item.quality}</span>}
                      <span className={`rounded-full px-2 py-1 ${item.valid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {item.valid ? copy.valid : copy.invalid}
                      </span>
                      {!item.active && <span className="rounded-full bg-amber-100 text-amber-700 px-2 py-1">{copy.inactive}</span>}
                    </div>
                  </div>
                  <Button size="xs" variant="outline" onClick={() => navigate(item.editPath)}>
                    <ExternalLink className="h-4 w-4 mr-1" /> {copy.view}
                  </Button>
                </div>
                <div className="flex justify-between gap-3 text-xs text-gray-500 mt-3">
                  <span>{item.contentLength ? formatBytes(item.contentLength) : '—'}</span>
                  <span>{new Date(item.uploadedAt).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>

          {!!detailQuery.data?.totalElements && (
            <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
              <span className="text-sm text-gray-500">
                {copy.results.replace('{count}', String(detailQuery.data.totalElements))}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={detailPage === 0} onClick={() => setDetailPage((page) => page - 1)}>
                  {copy.previous}
                </Button>
                <Button variant="outline" size="sm" disabled={detailPage + 1 >= detailQuery.data.totalPages} onClick={() => setDetailPage((page) => page + 1)}>
                  {copy.next}
                </Button>
              </div>
            </div>
          )}
        </div>
      </SideDrawer>
    </div>
  );
};

export default ContentProductivityReport;
